import jsPDF from "jspdf";
import { loadLogoForPdf } from "../convocations/logoLoader";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
};
const fmtMoney = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

/**
 * Certificato fiscale ai sensi dell'art. 15, comma 1, lett. i-quinquies del TUIR
 * (detrazione 19% spese sportive dilettantistiche per ragazzi 5-18 anni, max € 1.000/anno).
 *
 * data = { team, player, season, total_paid, items: [{description, amount, paid_date}] }
 * recipient = nome genitore destinatario (dichiarante)
 */
export async function downloadTaxCertificatePdf({ data, recipient }) {
  const { team, player, season, items, total_paid } = data;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 50;
  let y = margin;

  // Letterhead
  const logo = await loadLogoForPdf(team);
  if (logo) {
    try { doc.addImage(logo.dataUrl, logo.format, margin, y, 60, 60); } catch (e) { console.warn(e); }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(team?.legal_name || team?.name || "", pageW - margin, y + 10, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80);
  if (team?.address) doc.text(team.address, pageW - margin, y + 24, { align: "right" });
  if (team?.fiscal_code) doc.text(`Codice Fiscale: ${team.fiscal_code}`, pageW - margin, y + 36, { align: "right" });
  if (team?.vat_number) doc.text(`Partita IVA: ${team.vat_number}`, pageW - margin, y + 48, { align: "right" });
  if (team?.figc_affiliation_code) doc.text(`Matricola FIGC: ${team.figc_affiliation_code}`, pageW - margin, y + 60, { align: "right" });

  y += 90;
  doc.setTextColor(20);

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("CERTIFICAZIONE PAGAMENTI", pageW / 2, y, { align: "center" });
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("ai fini della detrazione fiscale per spese sportive dilettantistiche", pageW / 2, y + 16, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text("(Art. 15, comma 1, lett. i-quinquies del D.P.R. 22 dicembre 1986, n. 917 - TUIR)", pageW / 2, y + 30, { align: "center" });
  y += 50;
  doc.setTextColor(20);

  // Recipient
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Stagione sportiva: ${season || team?.season || "—"}`, pageW - margin, y, { align: "right" });
  y += 16;

  doc.setFontSize(11);
  doc.text(`Si certifica che il/la Sig./Sig.ra ${recipient || player?.parent_name || "________________"},`, margin, y);
  y += 16;
  doc.text("ha versato a questa Associazione/Società Sportiva Dilettantistica le somme di seguito indicate", margin, y);
  y += 14;
  doc.text(
    `per la pratica sportiva del minore ${player?.last_name || ""} ${player?.first_name || ""}, nato/a il ${fmtDate(player?.birth_date)}${player?.fiscal_code ? ` (C.F. ${player.fiscal_code})` : ""}.`,
    margin, y, { maxWidth: pageW - margin * 2 }
  );
  y += 28;

  // Table
  doc.setFillColor(241, 245, 249);
  doc.rect(margin, y, pageW - margin * 2, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text("DATA", margin + 10, y + 14);
  doc.text("CAUSALE", margin + 100, y + 14);
  doc.text("IMPORTO", pageW - margin - 10, y + 14, { align: "right" });
  doc.setTextColor(20);
  y += 24;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  if (!items || items.length === 0) {
    doc.setTextColor(120);
    doc.text("Nessun pagamento registrato per la stagione selezionata.", margin + 10, y + 14);
    doc.setTextColor(20);
    y += 22;
  } else {
    items.forEach((it) => {
      if (y > pageH - 180) {
        doc.addPage();
        y = margin;
      }
      doc.text(fmtDate(it.paid_date), margin + 10, y + 14);
      doc.text(it.description || "Quota associativa", margin + 100, y + 14, { maxWidth: pageW - margin - 100 - 100 });
      doc.text(fmtMoney(it.amount), pageW - margin - 10, y + 14, { align: "right" });
      doc.setDrawColor(230);
      doc.line(margin, y + 22, pageW - margin, y + 22);
      y += 22;
    });
  }

  // Total
  y += 6;
  doc.setFillColor(15, 23, 42);
  doc.rect(margin, y, pageW - margin * 2, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("TOTALE VERSATO", margin + 10, y + 18);
  doc.text(fmtMoney(total_paid), pageW - margin - 10, y + 18, { align: "right" });
  doc.setTextColor(20);
  y += 44;

  // Note
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(80);
  const note = `La presente certificazione è rilasciata ai fini della detrazione del 19% prevista per i compensi di iscrizione e abbonamento ad associazioni e società sportive dilettantistiche per ragazzi di età compresa tra i 5 e i 18 anni, nel limite massimo di € 1.000,00 per ciascun figlio (Art. 15, c. 1, lett. i-quinquies del TUIR).`;
  const noteLines = doc.splitTextToSize(note, pageW - margin * 2);
  doc.text(noteLines, margin, y, { lineHeightFactor: 1.4 });
  y += 11 * noteLines.length + 14;

  // Signature
  doc.setFont("helvetica", "normal");
  doc.setTextColor(20);
  doc.setFontSize(10);
  doc.text(`Data di emissione: ${fmtDate(new Date().toISOString())}`, margin, y);
  y += 20;
  doc.setDrawColor(120);
  doc.line(pageW - margin - 220, y + 26, pageW - margin, y + 26);
  doc.setFontSize(8);
  doc.text("Timbro e firma del Presidente / Legale rappresentante", pageW - margin - 220, y + 38);
  if (team?.president_name) doc.text(team.president_name, pageW - margin - 220, y + 52);

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(140);
  doc.text(
    `${team?.name || ""} · Certificazione generata il ${new Date().toLocaleString("it-IT")} · Documento valido ai fini fiscali`,
    pageW / 2, pageH - 20, { align: "center" }
  );

  const safe = `certificato_fiscale_${(player?.last_name || "x")}_${season || ""}`.toLowerCase().replace(/[^a-z0-9_]/g, "_");
  doc.save(`${safe}.pdf`);
}
